import { Button } from '@artosera/ui';
import { forwardRef, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import type { GalerieSession } from '../../api/types.js';
import { FAMILLES_CARTE } from './catalogue.js';
import {
  lienDuModuleActif,
  tuileVisible,
  tuilesDeLaCarte,
  type FiltreCarte,
  type ModulesAVenir,
} from './modulesAVenir.js';
import { TuileModule } from './TuileModule.js';
import styles from './CarteDesModules.module.css';

export interface CarteDesModulesProps {
  readonly galerie: GalerieSession;
  readonly galerieSlug: string;
  readonly avenir: ModulesAVenir;
  readonly filtre: FiltreCarte;
  readonly onFiltre: (filtre: FiltreCarte) => void;
}

/**
 * La carte des modules (maquette Halo « cartes par palier ») : tous les modules
 * de la régie rangés par famille, les actifs qui s'ouvrent, les autres visibles
 * et fermés. Repliée sur le prochain palier ; le compteur de chaque famille suit
 * le filtre, et une famille sans module sous le filtre disparaît.
 *
 * Statique : aucune tuile à venir ne mène ailleurs qu'à sa fiche.
 */
export const CarteDesModules = forwardRef<HTMLHeadingElement, CarteDesModulesProps>(
  function CarteDesModules(
    { galerie, galerieSlug, avenir, filtre, onFiltre },
    refTitre,
  ): JSX.Element {
    const { t } = useTranslation('regie');
    const tuiles = tuilesDeLaCarte(galerie, avenir);
    const visibles = tuiles.filter((tuile) => tuileVisible(tuile, filtre));

    const filtres: { cle: FiltreCarte; nombre: number }[] = [
      { cle: 'tout', nombre: tuiles.length },
      { cle: 'actifs', nombre: tuiles.filter((tuile) => !tuile.avenir).length },
      ...avenir.paliers.map((palier) => ({
        cle: palier as FiltreCarte,
        nombre: tuiles.filter((tuile) => tuile.palier === palier).length,
      })),
    ];

    const statut =
      filtre === 'tout'
        ? t('modulesAVenir.carte.statut.tout', { count: visibles.length })
        : filtre === 'actifs'
          ? t('modulesAVenir.carte.statut.actifs', { count: visibles.length })
          : t('modulesAVenir.carte.statut.palier', {
              count: visibles.length,
              palier: t(`modulesAVenir.palier.${filtre}`),
            });

    return (
      <section
        className={styles.carte}
        id="carte-des-modules"
        aria-labelledby="carte-des-modules-titre"
        data-ui="module-map"
      >
        <div className={styles.tete}>
          <h2 id="carte-des-modules-titre" tabIndex={-1} ref={refTitre}>
            {t('modulesAVenir.carte.titre')}
          </h2>
          <p className={styles.intro}>{t('modulesAVenir.carte.intro', { count: tuiles.length })}</p>
        </div>

        <div
          className={styles.filtres}
          role="group"
          aria-label={t('modulesAVenir.carte.filtresAria')}
        >
          {filtres.map(({ cle, nombre }) => (
            <button
              key={cle}
              type="button"
              className={styles.filtre}
              aria-pressed={filtre === cle}
              data-filtre={cle}
              onClick={() => onFiltre(cle)}
            >
              {t(`modulesAVenir.carte.filtres.${cle}`)}
              <span className={styles.nombre}>{nombre}</span>
            </button>
          ))}
        </div>
        <p className={styles.statut} role="status" aria-live="polite">
          {statut}
        </p>

        <div className={styles.familles}>
          {FAMILLES_CARTE.map((famille) => {
            const deLaFamille = visibles.filter((tuile) => tuile.famille === famille);
            if (deLaFamille.length === 0) return null;
            return (
              <section key={famille} className={styles.famille} data-famille={famille}>
                <h3>
                  {t(`modulesAVenir.carte.familles.${famille}`)}
                  <span className={styles.nombre}>{deLaFamille.length}</span>
                </h3>
                <ul className={styles.tuiles}>
                  {deLaFamille.map((tuile) => (
                    <li key={tuile.module} data-module={tuile.module}>
                      <TuileModule
                        module={tuile.module}
                        galerieSlug={galerieSlug}
                        etat={tuile.avenir ? 'avenir' : 'actif'}
                        lectureSeule={tuile.lectureSeule}
                        {...(tuile.palier ? { palier: tuile.palier } : {})}
                        to={
                          tuile.avenir
                            ? null
                            : lienDuModuleActif(galerie, galerieSlug, tuile.module)
                        }
                      />
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>

        {filtre !== 'tout' ? (
          <p className={styles.toute}>
            <Button variant="ghost" size="sm" onClick={() => onFiltre('tout')}>
              {t('modulesAVenir.carte.voirToute')}
            </Button>
          </p>
        ) : null}
      </section>
    );
  },
);
